import integrations from './Modules/integrations';
import scheduler from './Modules/schedule';
import offerTemplates from './Modules/offer-template';
import scheduleAll from './Modules/schedule-all';
import contact from './Modules/contact';
import attachments from './Modules/attachments';
import offers from './Modules/offers';
import extAssessment from './Modules/ext-assessment';
import backgroundCheck from './Modules/background-check';
import workingHours from './Modules/working-hours';
import knockout from './Modules/knockout';
import requisitions from './Modules/requisitions';
import fraudDetector from './Modules/fraud-detector';
import availabilityScheduler from './Modules/availability-scheduler';
import consent from './Modules/consent';
import rejection from './Modules/rejection';
import users from './Modules/users';

const ERROR_MODULES = {
  integrations,
  scheduler,
  offerTemplates,
  scheduleAll,
  contact,
  attachments,
  offers,
  extAssessment,
  backgroundCheck,
  workingHours,
  knockout,
  requisitions,
  fraudDetector,
  availabilityScheduler,
  consent,
  rejection,
  users,
};

const DEFAULT_ERROR_MESSAGE =
  'Something went wrong. Please try again later or contact support@kula.ai';

/**
 * First server error, whichever envelope it came in: a 2xx-with-errors body
 * gives `error: [...]`, a non-2xx gives `error: { errors: [...] }`.
 */
const getFirstServerError = (result) =>
  result?.errors?.[0] ??
  result?.error?.errors?.[0] ??
  result?.error?.[0] ??
  result?.[0];

/**
 * Extracts the most specific error information from the result object.
 * Priority:
 * 1. result.errors[0].reason (if result.errors[0] is an object with 'reason')
 * 2. result.errors[0] (if it exists)
 * 3. result.error[0] (if it exists)
 * 4. result[0] (if it exists)
 * Returns undefined if none are found.
 */
function getErrorKey(result) {
  let errorKey = getFirstServerError(result);

  // Bulk packages each failure as `{ id, reason }`. Unwrap rather than return
  // early, so a coded reason still gets colon-split below.
  if (errorKey && typeof errorKey === 'object') {
    errorKey = errorKey.reason ?? errorKey.message ?? errorKey;
  }

  // If errorKey is a string and contains a colon, extract only the error code part
  // (e.g., "err_zinc_invitation_already_exists: Additional message" -> "err_zinc_invitation_already_exists")
  if (typeof errorKey === 'string' && errorKey.includes(':')) {
    return errorKey.split(':')[0].trim();
  }

  if (
    typeof errorKey === 'string' &&
    !errorKey.startsWith('err_') &&
    result?.error_code
  ) {
    return result.error_code;
  }

  // Some BE responses package the error as `flash.error[0] = [humanMessage, codeKey]`.
  // When errors[0] is a non-err_ human string we can't match by code, prefer
  // the nested codeKey so module maps (e.g. `access_denied`) can resolve.
  if (
    typeof errorKey === 'string' &&
    !errorKey.startsWith('err_') &&
    Array.isArray(result?.flash?.error?.[0]) &&
    typeof result.flash.error[0][1] === 'string'
  ) {
    return result.flash.error[0][1];
  }

  return errorKey;
}

const getErrorMessage = (result, type, customErrorHandler = null) => {
  const moduleErrors = ERROR_MODULES[type];
  if (!moduleErrors) return DEFAULT_ERROR_MESSAGE;

  const errorKey = getErrorKey(result);

  if (customErrorHandler) {
    const customMessage = customErrorHandler(errorKey, result);
    if (customMessage) {
      return customMessage;
    }
  }

  const errorValue = moduleErrors?.[errorKey];
  // Cross-cutting codes (e.g. err_contact_anonymized) resolve from `consent`
  // when the feature module doesn't list them.
  return (
    errorValue?.message || consent[errorKey]?.message || DEFAULT_ERROR_MESSAGE
  );
};

const getErrorMessages = (error, type) => {
  const moduleErrors = ERROR_MODULES[type];
  if (!moduleErrors) return [DEFAULT_ERROR_MESSAGE];
  const errors = error?.errors; // this will have actual server error code
  const errorValues = errors?.map(
    (errorKey) =>
      moduleErrors[errorKey]?.message ||
      consent[errorKey]?.message ||
      DEFAULT_ERROR_MESSAGE
  );
  return errorValues || [DEFAULT_ERROR_MESSAGE];
};

const handleKomboApiError = (errorKey, result) => {
  if (errorKey === 'err_kombo_api_failed') {
    const message = result?.details;
    if (message) {
      try {
        const parsedMessage = JSON.parse(message);
        const errorMessage =
          parsedMessage?.error?.message ||
          parsedMessage?.error?.message?.remote_error?.error ||
          parsedMessage?.error?.message?.remote_error ||
          'Validation failed';
        return errorMessage;
      } catch {
        return 'Something went wrong. Please try again later or contact support@kula.ai';
      }
    }
  }
  return null;
};

// HRIS push errors carry dynamic detail the static map can't: per-field hints in
// `field_errors`, or a raw provider message wrapped as `err_adding_to_hris: <msg>`.
const handleHrisPushError = (errorKey, result) => {
  // Per-field hints win when the BE sends them.
  const fieldErrors = result?.field_errors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    const hint = Object.values(fieldErrors).flat().filter(Boolean).join(' ');
    if (hint) return hint;
  }
  // Strip the wrapper prefix and surface the raw provider message.
  if (errorKey === 'err_adding_to_hris') {
    const raw = result?.errors?.[0];
    if (typeof raw === 'string') {
      const message = raw.slice(raw.indexOf(':') + 1).trim();
      if (message) return message;
    }
  }
  return null;
};

// Combined custom handler for all HRIS flows (Kombo dynamic + push wrappers).
const handleHrisApiError = (errorKey, result) =>
  handleKomboApiError(errorKey, result) ||
  handleHrisPushError(errorKey, result);

// Surface the trailing reason SpringVerify wraps behind the error code + HTTP
// status (e.g. "err_...: Bad Request: <reason>"); null falls back to the static message.
const SPRINGVERIFY_STATUS_PREFIX =
  /^\s*(Bad Request|Unprocessable Entity|Not Found|Forbidden|Unauthorized|Conflict|Internal Server Error|Service Unavailable):/i;

const handleSpringVerifyError = (errorKey, result) => {
  if (errorKey !== 'err_springverify_check_creation_failed') return null;
  const raw = result?.errors?.[0];
  if (typeof raw !== 'string' || !raw.includes(':')) return null;
  // Drop the leading `err_...:` code segment, then a leading HTTP status phrase
  // so only the reason remains. Colons inside the reason are preserved.
  const detail = raw
    .slice(raw.indexOf(':') + 1)
    .replace(SPRINGVERIFY_STATUS_PREFIX, '')
    .trim();
  return detail || null;
};

/**
 * First server error as a string, whichever envelope it arrived in.
 *
 * A 2xx-with-errors body reaches callers as `error: [...]`; a non-2xx reaches
 * them as `error: { errors: [...] }` (the raw response body). Bulk actions add
 * a third: an array of `{ id, reason }`. Callers should not have to know which.
 */
const getFirstServerErrorText = (result) => {
  const first = getFirstServerError(result);
  if (first && typeof first === 'object') {
    return first.reason ?? first.message;
  }
  return first;
};

/** A machine code rather than a sentence; codes may carry `: message`. */
const isErrorCode = (text) =>
  /^[a-z][a-z0-9_]*$/.test(text.split(':')[0].trim());

/**
 * The reject toasts print the backend's error verbatim, which suits the human
 * strings it usually sends — but `err_survey_link_required_in_email_body`
 * reached a customer's toast as a raw code (ENG-3828). Map the codes we know;
 * anything that isn't one is already readable, so it passes through untouched.
 */
const getRejectionErrorMessage = (result, raw) => {
  // Callers pass `response.error[0]`, `undefined` on a non-2xx — which left
  // the toast with a title and no explanation.
  const text =
    typeof raw === 'string' && raw ? raw : getFirstServerErrorText(result);

  if (typeof text !== 'string' || !text) return undefined;

  // Machine-shaped, not just `err_`-prefixed: bulk attaches a bare
  // `unprocessable_entity`. Sentences pass through untouched.
  return isErrorCode(text) ? getErrorMessage(result, 'rejection') : text;
};

/**
 * Server text → toast copy for one bulk failure. Mapped entries win; an
 * unmapped sentence is already readable and passes through; an unmapped
 * machine code would be gibberish in a toast, so it becomes the generic copy.
 */
const humanizeBulkMessage = (text, moduleKey) => {
  if (typeof text !== 'string' || !text) return null;

  const moduleErrors = ERROR_MODULES[moduleKey] || {};
  const mapped =
    moduleErrors[text]?.message ??
    moduleErrors[text.split(':')[0].trim()]?.message;
  if (mapped) return mapped;

  return isErrorCode(text) ? DEFAULT_ERROR_MESSAGE : text;
};

/**
 * Bulk endpoints answer 2xx with a per-item `[{ data, status, errors }]` array,
 * so a rejected item carries no HTTP error and `error` alone never surfaces it
 * — every caller checking only `error` reported success for a refused action
 * (ENG-3991).
 *
 * Returns toast copy when anything failed, or null when every item succeeded.
 */
const getBulkFailureMessage = (data, error, moduleKey = 'users') => {
  if (error) {
    const text = getFirstServerErrorText({ error }) ?? error?.message;
    return humanizeBulkMessage(text, moduleKey) || DEFAULT_ERROR_MESSAGE;
  }

  const failed = (Array.isArray(data) ? data : []).filter(
    (item) => item?.status !== 200
  );
  if (!failed.length) return null;

  const messages = [
    ...new Set(
      failed.flatMap((item) =>
        // `{ scope: { field: [msg] } }` from `errors.to_hash`, but a bare
        // `["err_x"]` shows up elsewhere — don't split that string into chars.
        Object.values(item?.errors || {}).flatMap((scope) =>
          scope && typeof scope === 'object'
            ? Object.values(scope).flat()
            : [scope]
        )
      )
    ),
  ]
    .map((message) => humanizeBulkMessage(message, moduleKey))
    .filter(Boolean);

  if (!messages.length) return DEFAULT_ERROR_MESSAGE;

  // Toasts don't scroll; name one cause and count the rest.
  return messages.length > 1
    ? `${messages[0]} (+${messages.length - 1} more)`
    : messages[0];
};

export {
  getBulkFailureMessage,
  getErrorMessage,
  getRejectionErrorMessage,
  getErrorMessages,
  handleKomboApiError,
  handleHrisPushError,
  handleHrisApiError,
  handleSpringVerifyError,
};
